import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Flex,
  Icon,
  type IconType,
  Spinner,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import { PublishButton } from '@/modules/drafts';
import { RunHistoryDialog } from '@/modules/runs-list-panel';

/** The phase's own action, rendered in place of Publish / Test / Run. */
export interface IRailPrimaryAction {
  label: string;
  icon: IconType;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  'data-testid'?: string;
}

/** Props for {@link RailAction}. */
export interface IRailActionProps {
  /** Tooltip text, and the button's label while the rail is open. */
  label: string;
  /** Overrides `label` in the tooltip and accessible name, e.g. to say more. */
  tooltip?: string;
  icon: IconType;
  isCollapsed: boolean;
  /** Kept mounted but out of the layout — see `DraftActionToolbar`. */
  isHidden?: boolean;
  variant?: 'default' | 'outline';
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
  'data-tour-id'?: string;
}

/**
 * A rail action: labelled while the rail is open, icon-only once it collapses.
 *
 * The span is what makes the tooltip fire while the button is disabled, which
 * a disabled button's swallowed pointer events would otherwise prevent.
 *
 * @param props - Component props. See {@link IRailActionProps}.
 * @returns The action
 */
export function RailAction({
  label,
  tooltip = label,
  icon,
  isCollapsed,
  isHidden = false,
  variant = 'outline',
  disabled,
  isLoading,
  onClick,
  ...anchors
}: IRailActionProps) {
  return (
    <Tooltip delayDuration={200} variant="primary">
      <TooltipTrigger asChild>
        {/* The class is what beats `inline-flex` in the cascade. */}
        <span
          hidden={isHidden}
          className={cn('inline-flex shrink-0', isHidden && 'hidden')}
        >
          <Button
            type="button"
            variant={variant}
            size={isCollapsed ? 'icon-sm' : 'sm'}
            disabled={disabled}
            isLoading={isLoading}
            onClick={onClick}
            aria-label={tooltip}
            data-testid={anchors['data-testid']}
            data-tour-id={anchors['data-tour-id']}
          >
            <Icon type={icon} size="xs" />
            {!isCollapsed && label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface IDraftActionToolbarProps {
  automationId: string;
  onRun: () => void;
  onTest?: () => void;
  canRun: boolean;
  canTest: boolean;
  isRunning: boolean;
  isTesting: boolean;
  isCollapsed: boolean;
  primaryAction?: IRailPrimaryAction;
  /** Shown only in the collapsed strip, directly below the run log button. */
  collapsedAction?: ReactNode;
}

/**
 * The draft's actions: a 56px row while the rail is open, a column of icons
 * once it collapses.
 *
 * Every child keeps its slot in both widths, hidden ones included. Remounting
 * `PublishButton` refetches the editor's automation query (`useCanPublish`
 * sets `refetchOnMount: 'always'`), which shows as the document reloading.
 *
 * @param props - Component props
 * @returns The action row
 */
export function DraftActionToolbar({
  automationId,
  onRun,
  onTest,
  canRun,
  canTest,
  isRunning,
  isTesting,
  isCollapsed,
  primaryAction,
  collapsedAction,
}: IDraftActionToolbarProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tDrafts } = useTranslation('drafts');
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);
  const isBusy = isRunning || isTesting;
  // A test's status shows above the document instead, so only a run takes
  // the row over.
  const yieldsToStatus = isRunning && !isCollapsed;

  return (
    <Flex
      align="center"
      justify={isCollapsed ? 'start' : 'end'}
      direction={isCollapsed ? 'column-reverse' : 'row'}
      gap={2}
      /* Both land the first control on the 56px centerline the chat header
         and document title bar share. */
      className={cn(
        'w-full shrink-0',
        isCollapsed ? 'pt-3' : yieldsToStatus ? 'min-h-14' : 'h-14',
      )}
      data-testid="draft-rail-run-control"
    >
      {yieldsToStatus && (
        <Flex
          align="center"
          justify="center"
          gap={2}
          width="full"
          className="rounded-xl border bg-layout px-3 py-2 shadow-sm"
          data-testid="draft-rail-run-status"
        >
          <Spinner size="sm" className="shrink-0" />
          <Text level="small" weight="medium">
            {tAutomation('automation.draftRail.running')}
          </Text>
        </Flex>
      )}

      {/* Before the run log in the DOM = below it in the reversed strip. */}
      {isCollapsed && collapsedAction}

      <RunHistoryDialog
        automationId={automationId}
        triggerSize="icon-sm"
        isDraft
        isOpen={runHistoryOpen}
        onOpenChange={setRunHistoryOpen}
        onRun={onRun}
        showTrigger={!yieldsToStatus}
      />

      {primaryAction && (
        <RailAction
          {...primaryAction}
          isCollapsed={isCollapsed}
          isHidden={yieldsToStatus}
          variant="default"
        />
      )}

      {!primaryAction && (
        <>
          <PublishButton
            automationId={automationId}
            renderTrigger={({ disabled, isLoading, onPublish }) => (
              <RailAction
                label={tDrafts('drafts.actions.publish')}
                icon="Rocket"
                isCollapsed={isCollapsed}
                isHidden={yieldsToStatus}
                disabled={disabled || isLoading || isBusy}
                isLoading={isLoading}
                onClick={onPublish}
                data-testid="publish-button"
                data-tour-id="run-page-publish-button"
              />
            )}
          />

          {onTest && (
            <RailAction
              label={tAutomation('automation.draftRail.test')}
              icon="FlaskConical"
              isCollapsed={isCollapsed}
              isHidden={yieldsToStatus}
              disabled={!canTest || isBusy}
              isLoading={isCollapsed && isTesting}
              onClick={onTest}
              data-testid="draft-rail-test-button"
            />
          )}

          {/* `settleQuillBuild` polls this test id as its "the draft carries
              code" signal, and the draft-creation tour points at the tour id. */}
          <RailAction
            label={tAutomation('automation.run.button')}
            icon="Play"
            isCollapsed={isCollapsed}
            isHidden={yieldsToStatus}
            variant="default"
            disabled={!canRun || isBusy}
            isLoading={isCollapsed && isRunning}
            onClick={onRun}
            data-testid="run-automation-button"
            data-tour-id="run-automation-button"
          />
        </>
      )}
    </Flex>
  );
}
